import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import axios from "axios";
import { toast } from "react-toastify";
import { fetchBookingsData } from "store/bookings/actions";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import "./Bookings.css";

const DriverBookings = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [openIndex, setOpenIndex] = useState(null);
    const [customerStatusBtn, setCustomerStatusBtn] = useState(false);
    const [bookingStatusBtn, setBookingStatusBtn] = useState(false);
    const [bookingPaidStatusBtn, setBookingPaidStatusBtn] = useState(false);
    const [bookingOnBoardStatusBtn, setBookingOnBoardStatusBtn] = useState(false);
    const token = localStorage.getItem("authToken");

    // redux state
    const { data, loading, error } = useSelector((state) => state.Bookings);

    useEffect(() => {
        dispatch(fetchBookingsData());
    }, [dispatch]);

    const formattedDate = (date) => {
        return dayjs(date).isSame(dayjs(), "day")
            ? "Today"
            : dayjs(date).format("YYYY-MM-DD");
    };

    const toggleDetails = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleAction = async (bookingId, customerId, action) => {
        setCustomerStatusBtn(true);
        const formData = new FormData();
        formData.append('booking_id', bookingId);
        formData.append('customer_id', customerId);
        formData.append('status', action);

        try {
            const res = await axios.post(
                process.env.REACT_APP_WEB_APP_URL + "/api/customer-booking-status",
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            if (action == 1) {
                toast.success('customer accepted successfully');
            } else {
                toast.success('customer rejected successfully');
            }

            dispatch(fetchBookingsData());
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update status");
        } finally {
            setCustomerStatusBtn(false);
        }
    };

    const handleBookingStatus = async (bookingId, status) => {
        setBookingStatusBtn(true);
        console.log(`Booking ID: ${bookingId}, Booking Status: ${status}`);
        const formData = new FormData();
        formData.append('booking_id', bookingId);
        formData.append('status', status);

        try {
            await axios.post(
                process.env.REACT_APP_WEB_APP_URL + "/api/booking-status-change",
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast.success('booking status update successfully');
            dispatch(fetchBookingsData());
        } catch (err) {
            console.log(err);
            toast.error(err.response?.data?.message || "Failed to update status");
        } finally {
            setBookingStatusBtn(false);
        }
    };

    const handleMarkAsPaid = async (bookingId, customerId) => {
        setBookingPaidStatusBtn(true);
        const formData = new FormData();
        formData.append("booking_id", bookingId);
        formData.append("customer_id", customerId);

        try {
            await axios.post(
                process.env.REACT_APP_WEB_APP_URL + "/api/booking-payment-collect-cash",
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast.success("Customer marked as paid");
            dispatch(fetchBookingsData());
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update payment");
        } finally {
            setBookingPaidStatusBtn(false);
        }
    };

    const handleMarkAsOnBoard = async (bookingId, customerId) => {
        setBookingOnBoardStatusBtn(true);
        const formData = new FormData();
        formData.append("booking_id", bookingId);
        formData.append("customer_id", customerId);

        try {
            await axios.post(
                process.env.REACT_APP_WEB_APP_URL + "/api/markas-onboard",
                formData,
                {
                    headers: { Authorization: `Bearer ${token}` },
                }
            );

            toast.success("Customer marked as on-board");
            dispatch(fetchBookingsData());
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to update on-board status");
        } finally {
            setBookingOnBoardStatusBtn(false);
        }
    };

    const getBookingButton = (status) => {
        switch (status) {
            case "pending":
                return { text: "Start Booking", next: 'ongoing', variant: "success" };
            case "ongoing":
                return { text: "Arrived", next: 'arrived', variant: "primary" };
            case "arrived":
                return { text: "Start Booking", next: 'start', variant: "success" };
            case "start":
                return { text: "Complete", next: 'complete', variant: "primary" };
            case "complete":
                return { text: "Completed", next: null, variant: "secondary" };
            default:
                return null;
        }
    };

    return (
        <div className="driver-bookings">
            {loading ? (
                Array(4)
                    .fill()
                    .map((_, i) => (
                        <div key={i} className="booking-list-item">
                            <div className="booking-summary d-flex align-items-center p-3">
                                <Skeleton circle width={50} height={50} className="me-3" />
                                <div style={{ flex: 1 }}>
                                    <Skeleton width={150} height={20} />
                                    <Skeleton width={120} height={15} />
                                    <Skeleton width={200} height={15} />
                                </div>
                                <Skeleton width={80} height={20} />
                            </div>
                        </div>
                    ))
            ) : error ? (
                <p className="text-danger">Error: {error}</p>
            ) : (
                data?.data?.map((item, index) => (
                    <div key={index} className="booking-list-item">
                        <div
                            className="booking-summary text-white"
                            onClick={() => item?.customers?.status == 'accepted' ? toggleDetails(index) : null}
                        >
                            <div className="summary-left">
                                <img
                                    src={item?.vehicle?.vehicle_media[0]?.url}
                                    alt="Driver"
                                    className="driver-avatar-sm"
                                />
                                <div>
                                    <h5>
                                        {item.vehicle.make_name} - {item.vehicle.license_plate}
                                    </h5>
                                    <p className="small text-white">
                                        {formattedDate(item.booking?.booking_date)} |{" "}
                                        {item.booking.booking_time}
                                    </p>
                                    <p className="small text-white">
                                        {item.booking.pickup_addr.split(",")[0]} →{" "}
                                        {item.booking.dest_addr.split(",")[0]}
                                    </p>
                                </div>
                            </div>
                            <div className="summary-right">
                                <div>
                                    <p>
                                        Seats: {item.booking.total_booked_customer}/
                                        {item.booking.seats_allowed}
                                    </p>
                                    <p>Rate: {item.booking.seat_rate} PKR</p>
                                </div>
                                <span
                                    className={`badge status ${item.booking.status}`}
                                >
                                    {item.booking.status}
                                </span>
                                {item?.customers?.status == 'accepted' ? (
                                    <i className={`fa ${openIndex === index ? "fa-chevron-up" : "fa-chevron-down"
                                        } ms-2`}
                                    ></i>
                                ) : (
                                    <span
                                        className={`badge bg-primary badge-secondary`}
                                    >
                                        {item.customers.status}
                                    </span>
                                )}
                            </div>
                        </div>

                        {openIndex === index && (
                            <div className="booking-details">
                                <p>
                                    <strong>Date:</strong> {item.booking.booking_date} |{" "}
                                    <strong>Time:</strong> {item.booking.booking_time}
                                </p>
                                <p>
                                    <strong>Pickup:</strong> {item.booking.pickup_addr}
                                </p>
                                <p>
                                    <strong>Destination:</strong> {item.booking.dest_addr}
                                </p>

                                <div className="vehicle-info">
                                    <img
                                        src={item.vehicle.vehicle_media[0].url}
                                        alt="Vehicle"
                                        className="vehicle-img"
                                    />
                                    <div>
                                        <p>
                                            {item.vehicle.make_name} {item.vehicle.model_name}{" "}
                                            {item.vehicle.year}
                                        </p>
                                        <p>Plate: {item.vehicle.license_plate}</p>
                                    </div>
                                </div>

                                <p>
                                    <strong>Seats:</strong> {item.booking.seats_allowed} |{" "}
                                    <strong>Booked:</strong> {item.booking.total_booked_customer}
                                </p>
                                <p>
                                    <strong>Rate:</strong> {item.booking.seat_rate} PKR
                                </p>
                                <p>
                                    <strong>Payment:</strong> {item.booking?.payment_method} (
                                    <span className={`status ${item.customers.payment_status}`}>
                                        {item.customers.payment_status}
                                    </span>
                                    )
                                </p>

                                {/* ==== Driver Details ==== */}
                                <div className="customers-list">
                                    <h6>Driver Details</h6>
                                    <div
                                        key={item?.driver?.id}
                                        className="customer-card d-flex align-items-center justify-content-between p-3 mb-2"
                                    >
                                        <div className="d-flex align-items-center">
                                            <img
                                                src={item?.driver?.image}
                                                alt="Driver"
                                                className="driver-avatar-sm me-3"
                                            />
                                            <div>
                                                <p className="mb-1 fw-bold">{item?.driver?.name}</p>
                                                <p className="mb-1 text-muted">
                                                    {item?.driver?.phone_code} {item?.driver?.mobno}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* ==== Tracking Section ==== */}
                                {item.booking.tracking && item.booking.tracking.length > 0 && (
                                    <div className="tracking-section mt-3">
                                        <h6>Booking Tracking</h6>
                                        <ul className="list-group">
                                            {item.booking.tracking.map((t, idx) => (
                                                <li
                                                    key={idx}
                                                    className="list-group-item d-flex justify-content-between align-items-center"
                                                >
                                                    <span className="fw-bold text-capitalize">
                                                        {t.status.replace("_", " ")}
                                                    </span>
                                                    <span className="text-muted small">{t.datetime}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))
            )}
        </div>
    );
};

export default DriverBookings;
